import classNames from 'classnames';
import React from 'react';
import { getInvoiceDueDateLabel } from '../../../utils/invoiceDateUtils';
import { scrollToElement } from '../../../utils/scrollUtils';
import { CheckmarkCircleIcon, ReceiptIcon, WarningCircleIcon } from '../../svgs/ExpressonSVGs';

type InvoiceSummaryInfoProps = {
    wrapperClasses?: string;
    invoiceCount?: number;
    unpaidInvoicesCount: number;
    firstUnpaidInvoice?: any;
    totalOverdueSum?: number;
    overdueInvoicesCount?: number;
    invoiceTabActive?: boolean;
    setInvoiceTabActive?: (active: boolean) => void;
}

const InvoiceSummaryInfo: React.FC<InvoiceSummaryInfoProps> = ({
    wrapperClasses = '',
    invoiceCount = 0,
    unpaidInvoicesCount,
    firstUnpaidInvoice,
    totalOverdueSum = 0,
    overdueInvoicesCount = 0,
    invoiceTabActive = true,
    setInvoiceTabActive = () => { }
}) => {

    /**
     * handleScrollClick
     * 
     * Scroll to target element by ID
     * 
     * @param IdTarget
     */
    const handleScrollClick = (IdTarget: string) => {
        // Switch to invoices tab if currently viewing statements
        if (!invoiceTabActive) {
            setInvoiceTabActive(true);
            // Add small delay to allow DOM to render before scrolling
            setTimeout(() => {
                scrollToElement(IdTarget);
            }, 100);
        } else {
            // Already on invoices tab, scroll immediately
            scrollToElement(IdTarget);
        }
    };

    // Format amount as currency
    const formatAmount = (amount: number | null): string => {
        if (amount === null || amount === undefined) return '0 kr';
        return (amount || 0).toLocaleString('sv-SE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' kr';
    };

    return (
        <div
            className={classNames(
                'inv-state-invoice-summary-info-widgets',
                wrapperClasses,
                { 'two': overdueInvoicesCount > 0 }
            )}
        >
            {/* Customer is new and dont have any invoices yet */}
            {invoiceCount === 0 && (
                <div className="inv-state-invoice-all-paid widget">
                    <span className="svg-icon-wrapper green">
                        <ReceiptIcon width="28" height="28" />
                    </span>
                    <div className="info-text-wrapper">
                        <span>Väntar på din första faktura</span>
                        <p className="large-paragraph">Inga fakturor att betala</p>
                        {/* <span className="green">Använd ditt tankkort för att tanka</span> */}
                    </div>
                </div>
            )}

            {/* Render if there are no unpaid invoices */}
            {invoiceCount > 0 && unpaidInvoicesCount === 0 && (
                <div className="inv-state-invoice-all-due widget">
                    <span className="svg-icon-wrapper green">
                        <CheckmarkCircleIcon width="28" height="28" />
                    </span>

                    <div className="info-text-wrapper">
                        <span>Nästa faktura</span>
                        <p className="large-paragraph">Allt betalt</p>
                        <span>Inga obetalda fakturor</span>
                    </div>
                </div>
            )}

            {/* Render if there are unpaid invoices */}
            {unpaidInvoicesCount > 0 && (
                <>
                    <div className="inv-state-invoice-all-due widget">
                        <span className="svg-icon-wrapper orange">
                            <ReceiptIcon width="28" height="28" />
                        </span>

                        <div className="info-text-wrapper">
                            <span>Kommande faktura</span>
                            <p className="large-paragraph">{firstUnpaidInvoice ? formatAmount(firstUnpaidInvoice.remains) : '0 kr'}</p>
                            <span className="">
                                {firstUnpaidInvoice
                                    ? getInvoiceDueDateLabel(firstUnpaidInvoice.dueDate)
                                    : 'Förfaller N/A'}
                            </span>
                        </div>
                    </div>
                    {/* Only show this if there is an overdue invoices */}
                    {overdueInvoicesCount > 0 && (
                        <div className="inv-state-invoice-over-due widget">
                            <span className="svg-icon-wrapper red">
                                <WarningCircleIcon width="28" height="28" />
                            </span>
                            <div className="info-text-wrapper">
                                <span>Förfallna fakturor</span>
                                <p className="large-paragraph">{formatAmount(totalOverdueSum)}</p>
                                <span
                                    className="red"
                                    onClick={() => handleScrollClick('invoice-list')}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            handleScrollClick('invoice-list');
                                        }
                                    }}
                                >
                                    {overdueInvoicesCount} {overdueInvoicesCount === 1 ? 'faktura' : 'fakturor'}</span>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default InvoiceSummaryInfo
